import type { ShiftStatus } from "@station/contracts";
import { Decimal, Money } from "@station/core";
import type { Tone } from "@station/ui";

/**
 * Vardiya ekranlarının SAF dönüşümleri: durum tonları, süre biçimi ve Türkiye biçimli sayı ↔ API dizgesi.
 *
 * Para her yerde **kuruş cinsinden `bigint` dizgesidir**; hiçbir aşamada `number` kullanılmaz (AGENTS.md §3.2).
 * Litre / ondalık değerler `Decimal` üzerinden nokta ondalıklı dizgeye çevrilir.
 */

export const SHIFT_STATUS_TONE: Record<ShiftStatus, Tone> = {
  OPEN: "success",
  CLOSING: "warning",
  CLOSED: "info",
  APPROVED: "neutral",
  CANCELLED: "danger",
};

/** Ayrıştırılamayan ondalık metin: sözleşmenin düzenine UYMAYAN bu işaret, hatayı ilgili alanda doğurur. */
export const INVALID_DECIMAL = "?";

/** "0,5" → "0.5"; "1.250,75" → "1250.75". Belirsiz / hatalı yazım sessizce yorumlanmaz. */
export function trTextToApiDecimal(text: string): string {
  const trimmed = text.trim();
  if (trimmed === "") return "";
  try {
    return Decimal.parseTr(trimmed).toString();
  } catch {
    return INVALID_DECIMAL;
  }
}

/** "0.5" → "0,5"; sondaki gereksiz sıfırlar yazılmaz (form alanı okunur kalsın). */
export function apiDecimalToTrText(value: string): string {
  const decimal = Decimal.from(value);
  const scale = (decimal.toString().split(".")[1] ?? "").length;
  return decimal.formatTr(scale);
}

/** Tam sayı alanı: geçersiz metin DİZGE olarak kalır ve Zod hatası o alanın altında görünür. */
export function trTextToInt(text: string): number | string {
  const trimmed = text.trim();
  return /^-?\d+$/.test(trimmed) ? Number(trimmed) : trimmed;
}

/** Kuruş dizgesi ("123456") → Türkiye biçimi ("1.234,56 ₺"; simge `formatTr`'den gelir). Boş değerde tire. */
export function formatMinor(value: string | null | undefined): string {
  return value === null || value === undefined || value === "" ? "—" : Money.fromMinor(value).formatTr();
}

/** Litre dizgesi ("12.5") → "12,500 L". */
export function formatLitres(value: string | null | undefined, scale = 3): string {
  return value === null || value === undefined || value === ""
    ? "—"
    : `${Decimal.from(value).formatTr(scale)} L`;
}

/**
 * "Ne zamandır açık": `18.09.2026 06:00` → "3 sa 12 dk". CANLI sayaç DEĞİLDİR (pano 30 sn'de bir tazelenir);
 * bu yüzden saniye gösterilmez. Bir günü aşan vardiya "1 gün 2 sa" olarak okunur — açık kalmış vardiya fark edilsin.
 */
/**
 * Vardiyada seçilebilecek kasalar: seçili istasyonun etkin kasaları + istasyona bağlı OLMAYAN (merkez) kasalar.
 * Başka istasyonun kasası listelenmez (API de reddeder: "Kasa bu şirkete / istasyona ait değil").
 */
export function registersForStation<T extends { stationId: string | null; isActive: boolean }>(
  registers: readonly T[] | undefined,
  stationId: string | null | undefined,
): T[] {
  return (registers ?? []).filter(
    (register) => register.isActive && (register.stationId === null || register.stationId === stationId),
  );
}

export function formatElapsed(fromIso: string, now: Date = new Date()): string {
  const from = new Date(fromIso).getTime();
  if (Number.isNaN(from)) return "—";
  const totalMinutes = Math.max(0, Math.floor((now.getTime() - from) / 60_000));
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) return `${days} gün ${hours} sa`;
  if (hours > 0) return `${hours} sa ${minutes} dk`;
  return `${minutes} dk`;
}

/** Liste süzgeçlerinin boş değerleri adres çubuğuna yazılmaz. */
export function compactSearch(values: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values).filter(([, value]) => value !== undefined && value !== null && value !== ""),
  );
}
